import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCallBackMutation, useGetCallbackAvailabilityQuery, useGetCallQuery } from '@/store/api';
import { useAppDispatch } from '@/store';
import { pushToast } from '@/store/uiSlice';
import { Button, ErrorState, Panel, SkeletonRows, Status } from '@/components/ui';
import { formatDateTime, formatDuration, formatPhone } from '@/utils/format';

interface TurnLatency {
  engineMs: number;
  ttsFirstByteMs: number;
  playbackMs: number;
  totalTurnMs: number;
  cacheHit: boolean;
  provider: string;
  promptKey: string;
}

interface TranscriptTurn {
  _id: string;
  speaker: 'CALLER' | 'SYSTEM';
  rawText: string;
  confidence?: number;
  matchedIntent?: string;
}

export function CallDetailPage() {
  const { id = '' } = useParams();
  const { data, isLoading, isError, refetch } = useGetCallQuery(id);

  if (isError) return <ErrorState message="Could not load this call." onRetry={refetch} />;

  const transcript = (data?.transcript ?? []) as TranscriptTurn[];

  return (
    <div className="space-y-4">
      <Link to="/calls" className="text-small text-slate hover:text-ink">
        Back to calls
      </Link>

      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="tabular text-display">
          {data ? formatPhone(data.callerNumber) : 'Call'}
        </h1>
        {data ? <Status value={data.status} /> : null}
        {data?.direction === 'OUTBOUND' ? (
          <span className="text-small text-slate">Callback</span>
        ) : null}
        {data ? (
          <span className="text-small text-slate">
            {formatDateTime(data.startedAt)} · {formatDuration(data.durationSeconds)}
          </span>
        ) : null}
        {data ? <CallBackButton callId={data._id} direction={data.direction} caller={data.callerNumber} /> : null}
      </div>

      <RecordingPanel
        url={(data as unknown as { recordingUrl?: string })?.recordingUrl}
        voices={
          (data as unknown as { recordings?: { role: 'CALLER' | 'ASSISTANT'; url: string; ready?: boolean }[] })
            ?.recordings ?? []
        }
        inProgress={data?.status === 'IN_PROGRESS'}
      />

      <LatencyPanel turns={((data as unknown as { turnLatency?: TurnLatency[] })?.turnLatency) ?? []} />

      <UsagePanel usage={(data as unknown as { agentUsage?: AgentUsage })?.agentUsage} />

      <Panel title="What was said">
        {isLoading ? (
          <SkeletonRows rows={6} />
        ) : (
          <ol className="divide-y divide-rule">
            {transcript.map((turn) => (
              <li key={turn._id} className="px-4 py-3">
                <div className="flex items-baseline gap-3">
                  <span
                    className={`w-16 shrink-0 text-micro ${
                      turn.speaker === 'CALLER' ? 'text-jade' : 'text-slate'
                    }`}
                  >
                    {turn.speaker === 'CALLER' ? 'Caller' : 'Line'}
                  </span>
                  <p className="text-small text-ink">{turn.rawText}</p>
                </div>

                {turn.speaker === 'CALLER' && turn.matchedIntent ? (
                  <p className="mt-1 pl-[4.75rem] text-micro text-slate">
                    Understood as {turn.matchedIntent.replace(/_/g, ' ').toLowerCase()}
                    {typeof turn.confidence === 'number'
                      ? ` · heard with ${Math.round(turn.confidence * 100)}% confidence`
                      : ''}
                  </p>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </Panel>
    </div>
  );
}


/**
 * How long the caller waited after they stopped speaking, broken into the parts
 * that can be fixed independently. Slow turns are flagged for review, not
 * treated as failures: the acceptable figure comes from real calls.
 */
interface AgentUsage {
  model: string;
  requests: number;
  promptTokens: number;
  cachedTokens: number;
  outputTokens: number;
  estimatedUsd: number | null;
  sttProvider: string;
  ttsProvider: string;
}

function UsagePanel({ usage }: { usage?: AgentUsage }) {
  if (!usage) return null;
  const cachedShare = usage.promptTokens ? Math.round((usage.cachedTokens / usage.promptTokens) * 100) : 0;
  const cost = usage.estimatedUsd === null ? '—' : `$${usage.estimatedUsd.toFixed(4)}`;

  return (
    <Panel title="Model usage">
      <div className="px-4 py-3 text-sm text-slate">
        <p>
          <span className="text-ink">{usage.model}</span> · {usage.requests} requests ·{' '}
          <span className="tabular text-ink">{usage.promptTokens.toLocaleString()}</span> prompt tokens (
          <span className="tabular">{cachedShare}%</span> from cache) ·{' '}
          <span className="tabular text-ink">{usage.outputTokens.toLocaleString()}</span> output
        </p>
        <p className="mt-1">
          Estimated model cost <span className="tabular text-ink">{cost}</span> · listening {usage.sttProvider} ·
          speaking {usage.ttsProvider}
        </p>
      </div>
    </Panel>
  );
}

function LatencyPanel({ turns }: { turns: TurnLatency[] }) {
  if (turns.length === 0) return null;

  const worst = Math.max(...turns.map((turn) => turn.totalTurnMs));
  const average = Math.round(
    turns.reduce((sum, turn) => sum + turn.totalTurnMs, 0) / turns.length
  );

  return (
    <Panel title="Response speed">
      <div className="px-4 py-3">
        <p className="text-small text-slate">
          Average <span className="tabular text-ink">{average} ms</span> from the caller
          finishing to hearing a reply, worst <span className="tabular text-ink">{worst} ms</span>
          {' '}across {turns.length} {turns.length === 1 ? 'turn' : 'turns'}.
        </p>
      </div>

      <table className="w-full">
        <thead>
          <tr className="border-y border-rule text-left text-micro text-slate">
            <th className="px-4 py-2 font-medium">Turn</th>
            <th className="px-4 py-2 font-medium">Engine</th>
            <th className="px-4 py-2 font-medium">Voice first byte</th>
            <th className="px-4 py-2 font-medium">Playback</th>
            <th className="px-4 py-2 font-medium">Total</th>
            <th className="px-4 py-2 font-medium">Source</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-rule">
          {turns.map((turn, index) => (
            <tr key={index} className={turn.totalTurnMs > 1200 ? 'bg-amber-soft' : undefined}>
              <td className="px-4 py-2 text-small text-slate">
                {turn.promptKey.replace(/_/g, ' ').toLowerCase()}
              </td>
              <td className="tabular px-4 py-2 text-small">{turn.engineMs} ms</td>
              <td className="tabular px-4 py-2 text-small">{turn.ttsFirstByteMs} ms</td>
              <td className="tabular px-4 py-2 text-small">{turn.playbackMs} ms</td>
              <td className="tabular px-4 py-2 text-small font-medium">
                {turn.totalTurnMs} ms
                {turn.totalTurnMs > 1200 ? (
                  <span className="ml-2 text-micro text-amber">slow turn</span>
                ) : null}
              </td>
              <td className="px-4 py-2 text-micro text-slate">
                {turn.cacheHit ? 'cached' : turn.provider}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}


/**
 * Call recording playback, when the location has recording switched on.
 *
 * The URL is a short-lived presigned link generated per request, so nothing
 * here is a durable public address for a customer's conversation.
 */
function RecordingPanel({
  url,
  voices,
  inProgress
}: {
  url?: string;
  voices: { role: 'CALLER' | 'ASSISTANT'; url: string; ready?: boolean }[];
  inProgress: boolean;
}) {
  if (!url && voices.length === 0) return null;
  const pending = voices.some((v) => v.ready === false);

  return (
    <Panel title="Recording">
      <div className="space-y-3 px-4 py-3">
        {url ? (
          <div>
            <audio controls src={url} className="w-full">
              Your browser cannot play this recording.
            </audio>
            <p className="mt-1.5 text-micro text-slate">
              This link expires shortly. Reload the page to play it again.
            </p>
          </div>
        ) : null}
        {voices.map((voice) => (
          <div key={voice.role}>
            <p className="mb-1 text-micro font-medium text-slate">
              {voice.role === 'CALLER' ? 'Caller' : 'Assistant'}
            </p>
            {voice.ready === false ? (
              <p className="text-small text-slate">
                {inProgress ? 'Recording — the file is written when the call ends.' : 'No file was written for this voice.'}
              </p>
            ) : (
              <audio controls preload="none" src={voice.url} className="w-full">
                Your browser cannot play this recording.
              </audio>
            )}
          </div>
        ))}
        {voices.length > 0 ? (
          <p className="text-micro text-slate">
            Each voice is its own file, written as the call ends.
            {pending && inProgress ? ' Reload the page once the call is over.' : ''}
          </p>
        ) : null}
      </div>
    </Panel>
  );
}

/**
 * Rings the customer back from the number they called, with the agent on the
 * line. Shown only for phone calls that came in, and only when the server has
 * outbound calling set up; the API decides whether this number may be called.
 */
function CallBackButton({
  callId,
  direction,
  caller
}: {
  callId: string;
  direction?: 'INBOUND' | 'OUTBOUND';
  caller: string;
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: availability } = useGetCallbackAvailabilityQuery();
  const [callBack, { isLoading }] = useCallBackMutation();

  if (!availability?.available || direction === 'OUTBOUND' || !caller.startsWith('+')) return null;

  const onClick = async () => {
    if (!window.confirm(`Call ${formatPhone(caller)} back now? The assistant will speak to them.`)) return;
    const result = await callBack(callId);
    if ('error' in result) {
      const error = result.error as { data?: { error?: { message?: string } } };
      dispatch(pushToast(error.data?.error?.message ?? 'Could not place the call.', 'error'));
      return;
    }
    dispatch(pushToast('Calling now. The assistant will greet them when they answer.', 'success'));
    navigate(`/calls/${result.data.callId}`);
  };

  return (
    <Button variant="secondary" className="ml-auto" onClick={onClick} loading={isLoading}>
      Call back
    </Button>
  );
}
